import React from 'react';
import { Tab, TabItem, ContainerLayout, PromoBlock, Fieldset, Tile } from '@emorg-prd/standard-react';
import './Home.css';
import HomeCard from './HomeCard';
import { CardList } from '../common/components/Cards';
import { Button } from '../common/components/Button';
import HomeCardData, { HomeCardTag } from '../../utils/HomeCardData';
import { authenticatedUser } from '../../authentication/authModule';
import { UserRole } from '../../models/enums/UserRole';

interface HomeProps {}

interface HomeState {
	textToSearch: string;
}

export default class Home extends React.Component<HomeProps, HomeState> {
	cards: HomeCardData[];

	constructor(props) {
		super(props);

		this.cards = [
			{
				title: 'Azure Subscriptions',
				description:
					'Request a new subscription or view details associated with a subscription. Additionally, subscription owners can filter and export a list of subscription details.',
				startingLink: '/subscriptions',
				actions: [
					'Request a New Subscription',
					'Filter and export subscription details',
					'Manage roles',
					'View Cost Report (only UIT subscriptions)',
					'Request Policy Exception',
				],
				tags: [HomeCardTag.Subscriptions],
			},
			{
				title: 'AWS Accounts',
				description: 'Request a new account or view details associated with an account.',
				startingLink: '/awsAccounts',
				actions: ['Request a New Account', 'Filter account details', 'Request AWS PORs'],
				tags: [HomeCardTag.AWS],
			},
			{
				title: 'Entra ID Applications',
				description:
					'View your Entra ID applications and see which applications interact with your applications.',
				actions: [
					'Create a New Enterprise App',
					'View Application Permissions',
					'Grant Admin Consent',
					'Enable Seamless Single Sign-On',
				],
				startingLink: '/adApps',
				tags: [HomeCardTag.AzureAD],
			},
			// {
			// 	title: 'Entra ID 3rd Party Applications',
			// 	description: 'View your Entra ID 3rd Party applications and see which applications interact with your applications.',
			// 	actions: [
			// 		'Create a New 3rd Party App',
			// 		'View Application Permissions',
			// 		'Grant Admin Consent',
			// 		'Enable Seamless Single Sign-On',
			// 	],
			// 	startingLink: '/ad3rdPartyApps',
			// 	tags: [HomeCardTag.AzureAD],
			// },
			{
				title: 'Cloud Account Management',
				description:
					'Request a new cloud-only account for a specialized use case or reset your password for an existing cloud-only account.',
				actions: ['Request Cloud-only Account', 'Delete Cloud-only Account', 'Reset Password'],
				startingLink: '/cloudAccounts',
				tags: [HomeCardTag.AzureAD],
			},
			/* {
				title: 'Alibaba Ram User Management',
				description: 'Request a new Alibaba Ram User or reset your password for an existing Alibaba Ram User.',
				actions: [
					'Request Ram User',
					'Delete Ram User',
					'Reset Password',
				],
				startingLink: '/alibabaCloud',
				tags: [HomeCardTag.AzureAD],
			}, */
			{
				title: 'Cloud Groups Management',
				description:
					'You can create cloud-only groups to assign permissions to users at once without having to individually grant access.',
				startingLink: '/cloudGroups',
				tags: [HomeCardTag.AzureAD],
				actions: ['Create new Entra ID group', 'Manage group membership'],
			},
			{
				title: 'Container SSP',
				description: 'Container Self-Service Portal to manage your namespace(s).',
				actions: [
					'Request New Namespace',
					'Edit Your Namespace (Quota, RoleBindings, etc)',
					'Transfer Ownership',
					'Reattest to Owner R&R',
					'Delete Your Namespace',
					'Find Namespace Owner',
				],
				startingLink: 'https://goto/containerSSP',
				tags: [HomeCardTag.Containers],
			},
			{
				title: 'Container Status Dashboard',
				description:
					'The status of all OpenShift Clusters available in ExxonMobil supported by Container Platform team.' +
					'The dashboard shows status of systems and services mandatory for operating OpenShift Container Platforms which include' +
					'both On-premise and Cloud environment like Azure and AWS infrastructure.',
				actions: [
					'Azure infrastructure: Disconnected ARO and Connected ARO clusters',
					'AWS infrastructure: ROSA clusters',
					'On-premise BareMetal: Houston clusters',
				],
				startingLink: 'https://goto/containerstatus',
				tags: [HomeCardTag.Containers],
			},
			{
				title: 'Port Opening Requests: On-Premises / NECT Update',
				description:
					'Request a Port Opening from on-prem to internet or submit a NECT Update Request for Certified Internal or External Network Connections',
				actions: ['Request a Port Opening', 'Request a NECT Update'],
				startingLink: '/OnPremPortOpeningRequests',
				tags: [HomeCardTag.Network],
			},
			{
				title: 'Port Opening Requests: Connected Azure',
				description:
					'Request a Port Opening from your connected subscriptions to Internet or ExxonMobil Network.',
				actions: ['Request a Port Opening'],
				startingLink: '/portOpeningRequests/connectedAzure',
				tags: [HomeCardTag.Network],
			},
			{
				title: 'Port Opening Requests: AWS',
				description: 'Request a Port Opening Request from your connected aws account to Internet.',
				actions: ['Request a Port Opening Request'],
				startingLink: '/awsPortOpeningRequest/connectedAWS',
				tags: [HomeCardTag.Network, HomeCardTag.AWS],
			},
			{
				title: 'DPI (Deep Packet Inspection) / SSL Exception Requests: Connected Azure',
				description:
					'Request a DPI (Deep Packet Inspection) Exception Rule from your connected subscriptions to Internet.',
				actions: ['Request a DPI (Deep Packet Inspection) Exception Rule'],
				startingLink: '/doNotDecryptRequests/connectedAzure',
				tags: [HomeCardTag.Network],
			},
			{
				title: 'DNS Management',
				kicker: 'External Services',
				description: 'Create subdomains associated with the top-level domain xom.cloud.',
				actions: ['Request subdomain', 'Delegate zone manager', 'Txt record manager'],
				external: true,
				startingLink: 'https://goto/ddiforms',
				tags: [HomeCardTag.Network],
			},
			{
				title: 'Entra ID B2B',
				kicker: 'External Services',
				description:
					'Securely share applications and services with guests from any other trusted business organization.',
				actions: ['Invite external users (B2B)'],
				external: true,
				startingLink: 'http://goto/b2bbeta',
				tags: [HomeCardTag.AzureAD],
			},
			{
				title: 'SAML SSO Consultation',
				kicker: 'External Services',
				description:
					'Set up a new Entra ID Single Sign On application or request a SAML configuration update, maintenance or consulting.',
				additionalLinks: [
					{
						label: 'Set-up new SAML SSO consultation',
						href: 'https://goto/SSOForm',
					},
					{
						label: 'Request SAML App maintenance consultation',
						href: 'http://goto/SSO-Support',
					},
				],
				external: true,
				tags: [HomeCardTag.AzureAD],
			},
			{
				title: 'Azure Subscription Documentation',
				kicker: 'Documentation',
				description: 'Explore these cloudWiki references.',
				additionalLinks: [
					{
						label: 'How to request an Azure subscription',
						href: 'https://appwiki.xom.cloud/docs/admin/cloudssp/HowToRequestAnAzureSubscription.html',
					},
					{
						label: 'How to request a connected Azure subscription',
						href: 'https://appwiki.xom.cloud/docs/admin/cloudssp/HowToRequestAnAzureSubscription.html#how-to-request-a-connected-azure-subscription',
					},
					{
						label: 'Subscription Owner responsibilities',
						href: 'https://appwiki.xom.cloud/docs/admin/gettingstarted/OwnerResponsibilities.html',
					},
					{
						label: 'Subscription Owner Learning Path',
						href: 'https://appwiki.xom.cloud/docs/admin/gettingstarted/LearningPathSub.html',
					},
					{
						label: 'How to gather a permission report for your subscription',
						href: 'https://appwiki.xom.cloud/docs/admin/cloudssp/how-to-perform-access-review.html',
					},
					{
						label: 'Using Privileged Identity Management (PIM) for role management',
						href: 'https://appwiki.xom.cloud/docs/Id-Auth/pim.html',
					},
				],
				tags: [HomeCardTag.Subscriptions, HomeCardTag.Documentation],
			},
			{
				title: 'Entra ID Documentation',
				kicker: 'Documentation',
				description: 'Explore these cloudWiki references.',
				additionalLinks: [
					{
						label: 'How to reset your cloud account passwords',
						href: 'https://appwiki.xom.cloud/docs/admin/cloudssp/how-to-reset-password.html',
					},
					{
						label: 'Application Permissions overview',
						href: 'https://appwiki.xom.cloud/docs/Id-Auth/admin-consent.html',
					},
					{
						label: 'Entra ID Application Owner responsibilities',
						href: 'https://appwiki.xom.cloud/docs/Id-Auth/app-owner-responsibilities.html',
					},
					{
						label: 'Seamless Single Sign-On',
						href: 'https://appwiki.xom.cloud/docs/Id-Auth/SeamlessSSO.html',
					},
					{
						label: 'Client Secrets and Certificates',
						href: 'https://appwiki.xom.cloud/docs/Id-Auth/Client-Secrets-And-Certificates.html',
					},
					{
						label: 'How to reassign a Subscription Owner if assigned user left the company',
						href: 'https://appwiki.xom.cloud/docs/admin/cloudssp/LeaverProcessOfSubOwner.html',
					},
				],
				tags: [HomeCardTag.AzureAD, HomeCardTag.Documentation],
			},
			{
				title: 'Next steps with Terraform',
				description:
					'To start creating your Azure resources, learn more about Terraform best practices and next steps.',
				kicker: 'Documentation',
				additionalLinks: [
					{
						label: 'Terraform onboarding',
						href: 'https://goto/terraform',
					},
					{
						label: 'Terraform modules',
						href: 'https://goto/terraformModules',
					},
					{
						label: 'Terraform Repository Templates',
						href: 'https://goto/terraformTemplates',
					},
					{
						label: 'Terraform Enterprise Self Service Portal',
						href: 'https://goto/tfessp',
					},
					{
						label: 'Why should I use Infrastructure as Code?',
						href: 'https://www.developerportal.xom.cloud/articles/we-can-strike-oil-with-iac.mdx',
					},
				],
				tags: [HomeCardTag.Subscriptions],
			},
		].map((card) => new HomeCardData(card));

		this.state = {
			textToSearch: '',
		};
	}

	renderCards = (filter?) => {
		let cards = this.cards.filter((card) => card.contains(this.state.textToSearch));
		if (filter) {
			cards = cards.filter(filter);
		}

		return (
			<CardList>
				{cards.map((card, index) => (
					<HomeCard info={card} key={index} />
				))}
			</CardList>
		);
	};

	clearSearch = () => {
		this.setState({ textToSearch: '' });
	};

	render() {
		return (
			<>
				<ContainerLayout>
					<Fieldset>
						<div className="em-c-search__body">
							<input
								placeholder="Search for services"
								className="em-c-input"
								value={this.state.textToSearch}
								onChange={(ev) => {
									this.setState({
										textToSearch: ev.target.value,
									});
								}}
							/>
							<Button
								variant="tertiary"
								isLoading={false}
								disabled={false}
								name="Clear"
								loadingVariant="tertiary"
								onClick={this.clearSearch}
							/>
						</div>
					</Fieldset>
				</ContainerLayout>
				<ContainerLayout type="em-l-container">
					<Tab type="em-c-tabs--underline">
						<TabItem title="All">{this.renderCards()}</TabItem>
						<TabItem title="Subscriptions">
							{this.renderCards((card: HomeCardData) => card.tags.includes(HomeCardTag.Subscriptions))}
						</TabItem>
						<TabItem title="AWS">
							{this.renderCards((card: HomeCardData) => card.tags.includes(HomeCardTag.AWS))}
						</TabItem>
						<TabItem title="Entra ID Services">
							{this.renderCards((card: HomeCardData) => card.tags.includes(HomeCardTag.AzureAD))}
						</TabItem>
						<TabItem title="Network">
							{this.renderCards((card: HomeCardData) => card.tags.includes(HomeCardTag.Network))}
						</TabItem>
						<TabItem title="Documentation">
							{this.renderCards((card: HomeCardData) => card.tags.includes(HomeCardTag.Documentation))}
						</TabItem>
						<TabItem title="Containers">
							{this.renderCards((card: HomeCardData) => card.tags.includes(HomeCardTag.Containers))}
						</TabItem>
					</Tab>
				</ContainerLayout>
				<PromoBlock
					headline="Still need help?"
					desc="Get a Cloud Consultation today."
					style={{ textAlign: 'center' }}
				>
					<Button
						size="large"
						variant="primary"
						isLoading={false}
						disabled={false}
						name="Get Started"
						loadingVariant="primary"
						onClick={() => {
							window.open('https://goto/cloudconsult', '_blank');
						}}
					/>
				</PromoBlock>
			</>
		);
	}
}
